import React, { useEffect, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import GironeBadge from "@/components/GironeBadge";
import { api, publicFileUrl } from "@/lib/api";
import { Trophy, Crown, Flame, Ghost, Dice5, Sparkles, Medal, ShieldAlert } from "lucide-react";
import { AlboDoroSkeleton } from "@/components/Skeleton";

const SHIELD_PLACEHOLDER = "https://images.unsplash.com/photo-1577214322326-5b31d9d886b0?crop=entropy&cs=srgb&fm=jpg&w=300&q=80";

export default function AlboDoro() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/public/albo-doro")
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Impossibile caricare l'Albo d'Oro"));
  }, []);

  return (
    <div className="App">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden text-white"
               style={{ background: "radial-gradient(circle at 20% 0%, #0b1220 0%, #050914 45%, #000 100%)" }}>
        <div className="absolute inset-0 pointer-events-none opacity-40"
             style={{ background: "conic-gradient(from 200deg at 80% 30%, rgba(250,204,21,0.55) 0deg, transparent 60deg, rgba(59,86,224,0.35) 140deg, transparent 210deg, rgba(250,204,21,0.5) 330deg)" }} />
        <div className="absolute -top-10 -right-10 w-96 h-96 rounded-full blur-3xl opacity-40" style={{ background: "#facc15" }} />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-[10px] uppercase tracking-widest text-white/85">
            <Sparkles className="w-3.5 h-3.5" style={{ color: "#facc15" }} /> Fine Stagione {data?.season || "2026/27"}
          </div>
          <h1 className="display font-black text-5xl md:text-7xl leading-[1.02] mt-4 text-white drop-shadow-[0_4px_20px_rgba(0,0,0,0.5)] scale-in" data-testid="albo-doro-title">
            Albo <span style={{ color: "#facc15" }}>d'Oro</span>
          </h1>
          <p className="mt-4 text-white/85 max-w-2xl md:text-lg">
            Il Pantheon delle Fantaleghe Élite Italia: i migliori Fantallenatori, la più grande gufata, la sfida Tutto o Niente più clamorosa della stagione e molto altro.
          </p>
          {data?.stats && (
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl" data-testid="albo-doro-stats">
              <StatChip label="Squadre" value={data.stats.total_teams} />
              <StatChip label="Giornate" value={data.stats.matchdays_played} />
              <StatChip label="Gufate lanciate" value={data.stats.total_gufate} />
              <StatChip label="Tutto o Niente" value={data.stats.total_challenges_resolved} />
            </div>
          )}
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-16 space-y-14">
        {err && (
          <div className="rounded-2xl border border-red-200 bg-red-50 text-red-800 p-5 flex items-start gap-3" data-testid="albo-doro-error">
            <ShieldAlert className="w-5 h-5 shrink-0 mt-0.5" />
            <div>
              <div className="font-black">Nessun dato di stagione disponibile</div>
              <div className="text-sm opacity-80 mt-0.5">{String(err)}</div>
            </div>
          </div>
        )}

        {!data && !err && <AlboDoroSkeleton />}

        {data && (
          <>
            {/* Podio Campione */}
            <section data-testid="albo-podium">
              <SectionHead icon={Crown} color="#facc15" eyebrow="Classifica Generale" title="Il Campione FIFE" />
              {data.podium && data.podium.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mt-6 md:items-end">
                  {data.podium[1] && <PodiumCard place={2} team={data.podium[1]} />}
                  {data.podium[0] && <PodiumCard place={1} team={data.podium[0]} />}
                  {data.podium[2] && <PodiumCard place={3} team={data.podium[2]} />}
                </div>
              ) : (
                <EmptyState text="Classifica ancora vuota — chiudi la prima giornata per popolare l'albo." />
              )}
            </section>

            {/* Awards individuali */}
            <section data-testid="albo-awards">
              <SectionHead icon={Trophy} color="#3b56e0" eyebrow="Premi individuali" title="I re della stagione" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mt-6 stagger">
                <AwardCard
                  testid="award-capocannoniere"
                  icon={Flame}
                  accent="#f97316"
                  label="Capocannoniere"
                  subtitle="Fantapunti totali su tutte le giornate"
                  team={data.capocannoniere}
                  metric={data.capocannoniere && (<>
                    <div className="text-5xl md:text-6xl display font-black tabular-nums" style={{ color: "#f97316" }}>
                      {data.capocannoniere.total_points?.toFixed(1)}
                    </div>
                    <div className="text-white/60 text-xs mt-1">
                      {data.capocannoniere.matchdays_played} giornate · media {data.capocannoniere.avg?.toFixed(1)}
                    </div>
                  </>)}
                />
                <AwardCard
                  testid="award-prestazione"
                  icon={Medal}
                  accent="#facc15"
                  label="Prestazione della stagione"
                  subtitle="Miglior punteggio in una singola giornata"
                  team={data.prestazione_stagione}
                  metric={data.prestazione_stagione && (<>
                    <div className="text-5xl md:text-6xl display font-black tabular-nums" style={{ color: "#facc15" }}>
                      {data.prestazione_stagione.points?.toFixed(1)}
                    </div>
                    <div className="text-white/60 text-xs mt-1">Giornata {data.prestazione_stagione.matchday}</div>
                  </>)}
                />
                <AwardCard
                  testid="award-gufate"
                  icon={Ghost}
                  accent="#a78bfa"
                  label="Re delle Gufate"
                  subtitle="Più gufate riuscite lanciate contro gli avversari"
                  team={data.re_delle_gufate}
                  metric={data.re_delle_gufate && (<>
                    <div className="text-5xl md:text-6xl display font-black tabular-nums" style={{ color: "#a78bfa" }}>
                      {data.re_delle_gufate.wins}
                    </div>
                    <div className="text-white/60 text-xs mt-1">
                      su {data.re_delle_gufate.total_won_landed} gufate risolte in stagione
                    </div>
                  </>)}
                />
                <AwardCard
                  testid="award-tutto-o-niente"
                  icon={Dice5}
                  accent="#22d3ee"
                  label="Eroe Tutto o Niente"
                  subtitle="La sfida singola più clamorosa"
                  team={data.eroe_tutto_o_niente}
                  metric={data.eroe_tutto_o_niente && (<>
                    <div className="text-5xl md:text-6xl display font-black tabular-nums" style={{ color: "#22d3ee" }}>
                      +{data.eroe_tutto_o_niente.transfer}
                    </div>
                    <div className="text-white/60 text-xs mt-1">
                      Gufi vinti alla giornata {data.eroe_tutto_o_niente.matchday}
                      {data.eroe_tutto_o_niente.loser_team ? ` contro ${data.eroe_tutto_o_niente.loser_team}` : ""}
                    </div>
                  </>)}
                />
              </div>
            </section>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

function StatChip({ label, value }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 px-4 py-3">
      <div className="text-[10px] uppercase tracking-widest text-white/60">{label}</div>
      <div className="display font-black text-2xl md:text-3xl mt-0.5 tabular-nums">{value ?? "—"}</div>
    </div>
  );
}

function SectionHead({ icon: Icon, color, eyebrow, title }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${color}22`, color }}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500">{eyebrow}</div>
        <h2 className="display font-black text-2xl md:text-3xl tracking-tight text-slate-900">{title}</h2>
      </div>
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="mt-6 rounded-2xl border border-dashed border-slate-300 p-8 text-center text-slate-500">
      {text}
    </div>
  );
}

const PLACE_META = {
  1: { grad: "linear-gradient(140deg, #fef08a 0%, #eab308 45%, #a16207 100%)", ring: "#facc15", label: "Campione", scale: "md:scale-110", tag: "1° — Campione FIFE" },
  2: { grad: "linear-gradient(140deg, #e2e8f0 0%, #94a3b8 55%, #475569 100%)", ring: "#cbd5e1", label: "Vice", scale: "", tag: "2° posto" },
  3: { grad: "linear-gradient(140deg, #fed7aa 0%, #f97316 55%, #9a3412 100%)", ring: "#fdba74", label: "Terza", scale: "", tag: "3° posto" },
};

function PodiumCard({ place, team }) {
  const meta = PLACE_META[place];
  const shield = team.shield_path ? publicFileUrl(team.shield_path) : SHIELD_PLACEHOLDER;
  return (
    <div className={`relative rounded-3xl overflow-hidden text-white shadow-2xl ${meta.scale} transition-transform hover:scale-[1.03] duration-500 bounce-in ${place === 1 ? "shine-sweep" : ""}`}
         style={{ background: meta.grad, animationDelay: `${(3 - place) * 120}ms` }}
         data-testid={`podium-${place}`}>
      <div className="absolute inset-0 opacity-40 pointer-events-none"
           style={{ background: "radial-gradient(circle at 20% 10%, rgba(255,255,255,0.6) 0%, transparent 45%)" }} />
      <div className="relative p-6 md:p-7">
        <div className="flex items-center justify-between mb-4">
          <div className="inline-flex items-center gap-2 rounded-full bg-black/30 backdrop-blur px-3 py-1 text-[10px] font-black uppercase tracking-widest">
            {place === 1 ? <Crown className="w-3.5 h-3.5" /> : <Medal className="w-3.5 h-3.5" />}
            {meta.tag}
          </div>
          {team.girone && <GironeBadge girone={team.girone} size={22} />}
        </div>
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-white/95 overflow-hidden shrink-0 ring-2" style={{ ringColor: meta.ring }}>
            <img src={shield} alt={team.team_name}
                 className="w-full h-full object-cover"
                 onError={(e) => { e.currentTarget.src = SHIELD_PLACEHOLDER; }} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-widest text-white/75">{meta.label}</div>
            <div className="display font-black text-2xl md:text-3xl leading-tight truncate">{team.team_name}</div>
            {team.coach_name && (
              <div className="text-white/85 text-sm mt-0.5 truncate">Fantall.: {team.coach_name}</div>
            )}
          </div>
        </div>
        <div className="mt-5 grid grid-cols-3 gap-2 text-center">
          <MiniStat k="Punti" v={team.points ?? 0} />
          <MiniStat k="Fantavoto" v={team.total_points?.toFixed(1)} />
          <MiniStat k="DR" v={team.dr > 0 ? `+${team.dr}` : team.dr} />
        </div>
      </div>
    </div>
  );
}

function MiniStat({ k, v }) {
  return (
    <div className="rounded-xl bg-black/25 backdrop-blur px-2 py-2">
      <div className="text-[9px] uppercase tracking-widest text-white/70">{k}</div>
      <div className="font-black tabular-nums text-base">{v ?? "—"}</div>
    </div>
  );
}

function AwardCard({ icon: Icon, accent, label, subtitle, team, metric, testid }) {
  const shield = team?.shield_path ? publicFileUrl(team.shield_path) : SHIELD_PLACEHOLDER;
  return (
    <article className="relative rounded-3xl overflow-hidden text-white shadow-xl border border-white/5 transition-transform duration-500 hover:-translate-y-1 hover:shadow-2xl"
             style={{ background: "linear-gradient(150deg, #0b1220 0%, #111827 55%, #1f2937 100%)" }}
             data-testid={testid}>
      <div className="absolute -top-10 -right-10 w-56 h-56 rounded-full blur-3xl opacity-30 pointer-events-none" style={{ background: accent }} />
      <div className="relative p-6 md:p-7">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${accent}22`, color: accent }}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/60">{label}</div>
            <div className="text-white/85 text-sm">{subtitle}</div>
          </div>
        </div>
        {team ? (
          <div className="flex items-end gap-4">
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-white/95 overflow-hidden shrink-0">
              <img src={shield} alt={team.team_name} className="w-full h-full object-cover"
                   onError={(e) => { e.currentTarget.src = SHIELD_PLACEHOLDER; }} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/60">
                {team.girone && <GironeBadge girone={team.girone} size={14} />}
                <span>Girone {team.girone || "—"}</span>
              </div>
              <div className="display font-black text-xl md:text-2xl leading-tight truncate">{team.team_name}</div>
              {team.coach_name && <div className="text-white/70 text-sm truncate">{team.coach_name}</div>}
            </div>
            <div className="text-right shrink-0">{metric}</div>
          </div>
        ) : (
          <div className="text-white/60 text-sm py-4">Nessun dato ancora — completa qualche giornata e torna qui.</div>
        )}
      </div>
    </article>
  );
}
